import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact review summary + recent reviews list for the public event
 * detail sidebar / body. Designed to feed the parent's `event._reviews`
 * via `onLoaded` so JSON-LD picks up `aggregateRating` + `review[]`.
 */
export default function EventReviews({ eventId, onLoaded, limit = 5 }) {
  const [data, setData] = useState({ rating: 0, count: 0, items: [] });
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let alive = true;
    Promise.all([
      axios.get(`${API}/events/${eventId}/reviews/aggregate`),
      axios.get(`${API}/events/${eventId}/reviews?limit=20`),
    ]).then(([agg, list]) => {
      if (!alive) return;
      const next = {
        rating: agg.data?.rating || 0,
        count: agg.data?.count || 0,
        items: list.data?.reviews || [],
      };
      setData(next);
      if (onLoaded) onLoaded(next);
    }).catch(() => { if (alive) setData({ rating: 0, count: 0, items: [] }); });
    return () => { alive = false; };
  }, [eventId, onLoaded]);

  if (data.count === 0) return null;

  const visible = showAll ? data.items : data.items.slice(0, limit);

  return (
    <div className="space-y-3" data-testid="event-reviews">
      <div className="flex items-center gap-2" data-testid="event-reviews-summary">
        <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
        <span className="font-bold text-base">{data.rating.toFixed(1)}</span>
        <span className="text-xs text-muted-foreground">· {data.count} review{data.count === 1 ? '' : 's'}</span>
      </div>
      <div className="space-y-3">
        {visible.map(r => (
          <div key={r.review_id} className="rounded-lg border p-3 bg-card/40" data-testid={`review-row-${r.review_id}`}>
            <div className="flex items-center gap-1.5 mb-1">
              {[1, 2, 3, 4, 5].map(n => (
                <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />
              ))}
              <span className="text-[11px] text-muted-foreground ml-1">{r.reviewer_name}</span>
            </div>
            {r.title && <p className="font-medium text-sm">{r.title}</p>}
            {r.body && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line">{r.body}</p>}
          </div>
        ))}
      </div>
      {data.items.length > limit && (
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="text-xs text-indigo-400 hover:underline"
          data-testid="event-reviews-toggle"
        >
          {showAll ? 'Show less' : `View all ${data.count}`}
        </button>
      )}
    </div>
  );
}
